import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../hooks/useData";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, DataTable, StatusBadge, Button, Field, Select, Input, Modal } from "../components/shared";
import { num, fmtDate } from "../lib/api";
import { Printer, FileText, PackageCheck, CalendarClock } from "lucide-react";

export default function Challans() {
  const { data, loading, reload } = useData("/challans");
  const navigate = useNavigate();
  const [type, setType] = useState("all"); // jobber | inhouse | all
  const [jobber, setJobber] = useState("");
  const [process, setProcess] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [recv, setRecv] = useState(null);
  const [edExp, setEdExp] = useState(null);

  const submitExp = async (e) => {
    e.preventDefault();
    try {
      await api.put("/challan-expected-date", { challan_number: edExp.challan_number, expected_receiving_date: edExp.expected_receiving_date || null });
      toast.success("Expected delivery date updated"); setEdExp(null); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const all = useMemo(() => data || [], [data]);
  const jobberNames = useMemo(() => [...new Set(all.filter((r) => r.jobber_name).map((r) => r.jobber_name))].sort(), [all]);
  const processes = useMemo(() => [...new Set(all.map((r) => r.to_process).filter(Boolean))].sort(), [all]);

  const rows = useMemo(() => {
    return all.filter((r) => {
      if (type === "jobber" && !r.jobber_name) return false;
      if (type === "inhouse" && r.jobber_name) return false;
      if (jobber && r.jobber_name !== jobber) return false;
      if (process && r.to_process !== process) return false;
      if (status && r.status !== status) return false;
      if (q.trim()) {
        const s = q.trim().toLowerCase();
        const hay = `${r.challan_number || ""} ${r.design_number || ""} ${r.jobber_name || ""} ${r.production_code || ""} ${r.to_process || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [all, type, jobber, process, status, q]);

  const clear = () => { setJobber(""); setProcess(""); setStatus(""); setQ(""); };
  const hasFilter = jobber || process || status || q;

  const buildReceive = (title, jobberName, lots) => {
    const seen = {};
    const lines = (lots || [])
      .filter((l) => (l.pending_qty || 0) > 0)
      .map((l) => {
        const key = `${l.production_order_id || l.production_code || l.design_number}|${l.to_process}`;
        const joinsWith = l.as_garment_default ? seen[key] : null;
        if (l.as_garment_default && !joinsWith) seen[key] = l.challan_number || "another line";
        const due = l.as_garment_default && l.garments_due != null ? Math.min(l.pending_qty || 0, Math.max(0, l.garments_due)) : (l.pending_qty || 0);
        return { issue_id: l.id, selected: !joinsWith, challan_number: l.challan_number, to_process: l.to_process,
        design_number: l.design_number, item_type: l.item_type, colour: l.colour,
        is_part: !!l.is_part, part_name: l.part_name, as_garment: !!l.as_garment_default && !joinsWith, joins_with: joinsWith,
        total_qty: l.total_qty, received_already: l.received_qty || 0, rejected_already: l.rejected_qty || 0,
        pending: l.pending_qty || 0, received_qty: joinsWith ? 0 : due, rejected_qty: 0, rate: l.issue_rate || "" };
      });
    if (!lines.length) { toast.info("No pending lots to receive"); return; }
    setRecv({ title, jobber_name: jobberName, lines, full_close: true,
      actual_receiving_date: new Date().toISOString().slice(0, 10), jobber_bill_number: "" });
  };

  const openReceive = async (challanNumber) => {
    try {
      const { data: ch } = await api.get(`/challans/${challanNumber}`);
      const lots = (ch.lots || []).filter((l) => !l.merged && l.jobber_name).map((l) => ({ ...l, challan_number: ch.challan_number }));
      buildReceive(`Receive Challan ${ch.challan_number}`, ch.jobber_name, lots);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load challan"); }
  };

  const openReceiveJobber = async (jobberName) => {
    try {
      const { data: lots } = await api.get(`/receive/pending-lots`, { params: { jobber: jobberName } });
      buildReceive(`Receive all pending from ${jobberName}`, jobberName, lots);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load pending lots"); }
  };

  const setLine = (i, k, v) => setRecv((f) => ({ ...f, lines: f.lines.map((l, j) => j === i ? { ...l, [k]: v } : l) }));
  const toggleLine = (i) => setRecv((f) => ({ ...f, lines: f.lines.map((l, j) => j === i ? { ...l, selected: !l.selected } : l) }));
  const selectedLines = (recv?.lines || []).filter((l) => l.selected);
  const allSelected = (recv?.lines || []).length > 0 && selectedLines.length === (recv?.lines || []).length;
  const toggleAll = () => setRecv((f) => ({ ...f, lines: f.lines.map((l) => ({ ...l, selected: !allSelected })) }));
  const totRecv = selectedLines.reduce((s, l) => s + (parseFloat(l.received_qty) || 0), 0);
  const challanCount = new Set(selectedLines.map((l) => l.challan_number)).size;

  const submitReceive = async (e) => {
    e.preventDefault();
    const lines = recv.lines
      .filter((l) => l.selected && (parseFloat(l.received_qty) || 0) > 0)
      .map((l) => ({ issue_id: l.issue_id, rate: parseFloat(l.rate) || 0, as_garment: l.is_part ? !!l.as_garment : undefined }));
    if (!lines.length) { toast.error("Select at least one lot to receive"); return; }
    try {
      const { data: res } = await api.post("/process-receipts/multi", {
        lines, actual_receiving_date: recv.actual_receiving_date, jobber_bill_number: recv.jobber_bill_number });
      toast.success(`Received ${res.created} lot(s) into WIP`);
      if (res.errors?.length) toast(`${res.errors.length} lot(s) had issues — ${res.errors[0]}`);
      setRecv(null); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Receive failed"); }
  };

  const columns = [
    { key: "challan_number", header: "Challan No", mono: true, render: (r) => <span className="font-semibold text-blue-800">{r.challan_number}</span> },
    { key: "issue_date", header: "Date", render: (r) => fmtDate(r.issue_date || r.created_at) },
    { key: "jobber_name", header: "Jobber", render: (r) => r.jobber_name || <span className="text-slate-400">In-House</span> },
    { key: "to_process", header: "Process" },
    { key: "production_code", header: "Prod Order", mono: true, render: (r) => r.production_code || "—" },
    { key: "design_number", header: "Design", render: (r) => r.design_number || "—" },
    { key: "total_qty", header: "Qty", align: "right", render: (r) => num(r.total_qty) },
    { key: "expected_receiving_date", header: "Expected Return", render: (r) => (
      <button onClick={(e) => { e.stopPropagation(); setEdExp({ challan_number: r.challan_number, expected_receiving_date: (r.expected_receiving_date || "").slice(0, 10) }); }}
        className="inline-flex items-center gap-1 text-slate-700 hover:text-blue-700 group" data-testid={`challan-exp-edit-${r.challan_number}`}>
        <span>{fmtDate(r.expected_receiving_date)}</span>
        <CalendarClock size={13} className="opacity-40 group-hover:opacity-100" />
      </button>
    ) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", header: "", sortable: false, render: (r) => (
      <div className="flex items-center gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
        {r.has_pending && (
          <Button size="sm" onClick={() => openReceive(r.challan_number)} data-testid={`challan-receive-${r.challan_number}`}><PackageCheck size={13} /> Receive</Button>
        )}
        <Button size="sm" variant="secondary" onClick={() => navigate(`/challans/${r.challan_number}`)} data-testid={`challan-view-${r.challan_number}`}><Printer size={13} /> View</Button>
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Jobber Challans" subtitle="View, print & receive jobber challans. Receive a whole shared challan, or one-click receive every pending lot from a jobber across all challans." />

      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="challan-filters">
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="challan-type-toggle">
          {[["jobber", "Jobber"], ["inhouse", "In-House"], ["all", "All"]].map(([v, l]) => (
            <button key={v} onClick={() => setType(v)} data-testid={`challan-type-${v}`}
              className={`px-3 py-1.5 text-sm transition-colors ${type === v ? "bg-blue-800 text-white font-semibold" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <Field label="Jobber" className="min-w-[160px]">
          <Select value={jobber} onChange={(e) => setJobber(e.target.value)} placeholder="All jobbers" options={jobberNames.map((n) => ({ value: n, label: n }))} data-testid="challan-filter-jobber" />
        </Field>
        {jobber && (
          <Button onClick={() => openReceiveJobber(jobber)} data-testid="receive-all-jobber-btn" className="mb-0"><PackageCheck size={14} /> One-Click Receive All from {jobber}</Button>
        )}
        <Field label="Process" className="min-w-[150px]">
          <Select value={process} onChange={(e) => setProcess(e.target.value)} placeholder="All processes" options={processes.map((n) => ({ value: n, label: n }))} data-testid="challan-filter-process" />
        </Field>
        <Field label="Status" className="min-w-[140px]">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} placeholder="All statuses" options={[{ value: "Pending", label: "Pending" }, { value: "Partial", label: "Partial" }, { value: "Received", label: "Received" }, { value: "Closed", label: "Closed" }]} data-testid="challan-filter-status" />
        </Field>
        <Field label="Search" className="min-w-[200px] flex-1">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Challan / design / order…" data-testid="challan-search" />
        </Field>
        <div className="flex items-center gap-3 pb-0.5">
          <span className="text-sm text-slate-500" data-testid="challan-count"><b className="text-slate-800">{rows.length}</b> of {all.length}</span>
          {hasFilter && <Button size="sm" variant="secondary" onClick={clear} data-testid="challan-clear">Clear</Button>}
        </div>
      </div>

      {!loading && rows.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg py-16 text-center text-slate-400" data-testid="challan-empty">
          <FileText size={28} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No {type === "jobber" ? "jobber " : type === "inhouse" ? "in-house " : ""}challans found.</p>
        </div>
      ) : (
        !loading && <DataTable testid="challans-table" columns={columns} rows={rows} exportName="jobber-challans.csv" onRowClick={(r) => navigate(`/challans/${r.challan_number}`)} />
      )}

      <Modal open={!!recv} onClose={() => setRecv(null)} title={recv?.title || "Receive"} xwide>
        {recv && (
          <form onSubmit={submitReceive} className="space-y-4" data-testid="receive-challan-form">
            <div className="bg-slate-50 rounded-md p-3 text-sm flex flex-wrap gap-x-8 gap-y-1">
              <span><span className="text-slate-500">Jobber:</span> <b>{recv.jobber_name}</b></span>
              <span><span className="text-slate-500">Challans:</span> <b>{challanCount}</b></span>
              <span><span className="text-slate-500">Selected:</span> <b>{selectedLines.length}</b> / {recv.lines.length} lots</span>
            </div>

            <div className="border border-slate-200 rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                  <th className="px-3 py-2 text-left"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="receive-select-all" /></th>
                  {["Challan", "Design / Item", "Process", "Issued", "Already Recv", "Receiving Now", "Rate"].map((h, i) => <th key={i} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {recv.lines.map((l, i) => (
                    <tr key={l.issue_id} className={`border-t border-slate-100 ${l.selected ? "" : "opacity-40"}`} data-testid={`receive-line-${i}`}>
                      <td className="px-3 py-2"><input type="checkbox" checked={!!l.selected} onChange={() => toggleLine(i)} data-testid={`receive-check-${i}`} /></td>
                      <td className="px-3 py-2 font-mono text-xs text-blue-700 whitespace-nowrap">{l.challan_number || "—"}</td>
                      <td className="px-3 py-2"><div className="font-medium text-slate-800">{l.design_number || "—"}</div><div className="text-xs text-slate-500">{[l.item_type, l.colour].filter(Boolean).join(" · ")}</div></td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                        {l.to_process || "—"}
                        {l.is_part && (
                          <div className="text-xs">
                            <span className="text-violet-700 font-medium">Part: {l.part_name}</span>
                            <label className="flex items-center gap-1 mt-1 text-slate-600">
                              <input type="checkbox" checked={!!l.as_garment} onChange={() => setLine(i, "as_garment", !l.as_garment)} data-testid={`receive-as-garment-${i}`} />
                              Complete garments (parts joined)
                            </label>
                            {l.joins_with && <div className="text-amber-700 mt-1" data-testid={`receive-joins-with-${i}`}>Joined with {l.joins_with} — closes automatically</div>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular text-slate-500">{num(l.total_qty)}</td>
                      <td className="px-3 py-2 text-right tabular text-slate-500">{num(l.received_already + l.rejected_already)}</td>
                      <td className="px-3 py-2 text-right tabular font-semibold text-emerald-700" data-testid={`receive-qty-${i}`}>{num(l.received_qty)}</td>
                      <td className="px-3 py-2"><Input type="number" step="any" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} className="w-24" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-6 text-sm">
              <span className="text-slate-500">Total Receive: <b className="text-emerald-700" data-testid="receive-total">{num(totRecv)}</b></span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Actual Receiving Date"><Input type="date" value={recv.actual_receiving_date} onChange={(e) => setRecv({ ...recv, actual_receiving_date: e.target.value })} data-testid="receive-date" /></Field>
              <Field label="Jobber Bill No (optional)"><Input value={recv.jobber_bill_number} onChange={(e) => setRecv({ ...recv, jobber_bill_number: e.target.value })} data-testid="receive-billno" /></Field>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={() => setRecv(null)}>Cancel</Button>
              <Button type="submit" data-testid="receive-submit">Receive into WIP</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!edExp} onClose={() => setEdExp(null)} title="Edit Expected Delivery Date">
        {edExp && (
          <form onSubmit={submitExp} className="space-y-4" data-testid="challan-exp-form">
            <div className="bg-slate-50 rounded-md p-3 text-sm">Challan <b className="font-mono text-blue-800">{edExp.challan_number}</b></div>
            <Field label="Expected Delivery / Return Date"><Input type="date" value={edExp.expected_receiving_date || ""} onChange={(e) => setEdExp({ ...edExp, expected_receiving_date: e.target.value })} data-testid="challan-exp-date" /></Field>
            <p className="text-xs text-slate-400">Applies to all lots issued on this challan.</p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setEdExp(null)}>Cancel</Button>
              <Button type="submit" data-testid="challan-exp-save">Save</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
